import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Input,
  IconButton,
  SimpleGrid,
  Switch,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, type MouseEvent } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Settings2, TableProperties, UserRoundPen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { getPagePermissions, updatePagePermissions } from '../../api/settings/pagePermissions'
import {
  getAddEmployeePageSetup,
  getInflowPageSetup,
  updateAddEmployeePageSetup,
  updateInflowPageSetup,
} from '../../api/settings/pageSetup'
import { phoneNumbers, getCountryNameByCode } from '../countries/PhoneDial'
import ModalMultiSelect from '../forms/modalForms/modalMultiSelect'
import handleToast from '../HandleToast'
import {
  AddEmployeePageSetupConfig,
  InflowPageSetupConfig,
  PageSetupFieldConfig,
} from '../../types/settings/pageSetupTypes'

interface TabNode {
  key: string
  label: string
  children?: TabNode[]
  disabledRoles?: string[]
}

interface ModuleSection {
  key: string
  titleKey: string
  descriptionKey: string
  tabs: TabNode[]
}

const INFLOW_COLUMN_KEYS = [
  'name',
  'vacancy_title',
  'profile_completion',
  'it_access',
  'keys',
  'devices',
  'other_assets',
  'documents_status',
  'welcome_meeting_planned',
  'actions',
] as const

const INFLOW_COLUMN_LABEL_KEYS: Record<string, string> = {
  name: 'common.fields.name',
  vacancy_title: 'recruitment.inflow.columns.vacancy',
  profile_completion: 'recruitment.inflow.columns.profileData',
  it_access: 'recruitment.inflow.columns.itAccess',
  keys: 'recruitment.inflow.columns.keys',
  devices: 'recruitment.inflow.columns.devices',
  other_assets: 'recruitment.inflow.columns.otherAssets',
  documents_status: 'recruitment.inflow.columns.documents',
  welcome_meeting_planned: 'recruitment.inflow.columns.welcomeMeeting',
  actions: 'recruitment.inflow.columns.actions',
}

const ADD_EMPLOYEE_FIELD_ORDER = [
  'first_name',
  'last_name',
  'full_name',
  'local_name',
  'national_identification_number',
  'personal_email',
  'education_level',
  'nationality',
  'country_of_residence',
  'phone_number',
  'address',
  'city',
  'zip_code',
  'date_of_birth',
  'gender',
  'emergency_contact_email',
  'emergency_contact_phone',
  'emergency_contact_type',
  'emergency_contact_name',
  'work_email',
  'full_job_title',
  'employee_number',
  'department',
  'country_of_work',
  'teams',
  'current_position',
  'previous_position',
  'contract_type',
  'contract_duration',
  'start_date',
  'salary',
  'probation_end_date',
  'temporary_contract_end_date',
  'hours_per_week',
  'number_of_holidays',
  'hours_in_office',
  'hours_from_home',
  'primary_evaluator',
  'secondary_evaluator',
  'mentor',
  'career_start_date',
  'last_day',
] as const

const ADD_EMPLOYEE_FIELD_LABEL_KEYS: Record<string, string> = {
  first_name: 'common.fields.firstName',
  last_name: 'common.fields.lastName',
  full_name: 'workforce.people.fields.fullName',
  local_name: 'workforce.people.fields.localName',
  national_identification_number: 'workforce.people.fields.nationalIdNumber',
  personal_email: 'workforce.people.fields.personalEmail',
  education_level: 'workforce.people.fields.educationLevel',
  nationality: 'workforce.people.fields.nationality',
  country_of_residence: 'workforce.people.fields.countryOfResidence',
  phone_number: 'workforce.people.fields.phoneNumber',
  address: 'workforce.people.fields.address',
  city: 'workforce.people.fields.city',
  zip_code: 'workforce.people.fields.zipCode',
  date_of_birth: 'workforce.people.fields.dateOfBirth',
  gender: 'workforce.people.fields.gender',
  emergency_contact_email: 'workforce.people.fields.emergencyContactEmail',
  emergency_contact_phone: 'workforce.people.fields.emergencyContactNumber',
  emergency_contact_type: 'workforce.people.fields.emergencyContactType',
  emergency_contact_name: 'workforce.people.fields.emergencyContactName',
  work_email: 'workforce.people.fields.workEmail',
  full_job_title: 'workforce.people.fields.fullJobTitleShort',
  employee_number: 'workforce.people.fields.employeeNumber',
  department: 'workforce.people.fields.department',
  country_of_work: 'workforce.people.countryOfWork',
  teams: 'workforce.teams.teamName',
  current_position: 'workforce.people.fields.currentPosition',
  previous_position: 'workforce.people.fields.previousPosition',
  contract_type: 'workforce.mutations.fields.contractType',
  contract_duration: 'workforce.mutations.fields.contractDuration',
  start_date: 'workforce.people.fields.startDate',
  salary: 'workforce.people.fields.grossSalary',
  probation_end_date: 'workforce.people.fields.probationPeriodEndDate',
  temporary_contract_end_date: 'workforce.people.fields.temporaryContractEndDate',
  hours_per_week: 'workforce.mutations.fields.workHoursPerWeek',
  number_of_holidays: 'workforce.people.fields.holidaysPerYear',
  hours_in_office: 'workforce.mutations.fields.workHoursInOffice',
  hours_from_home: 'workforce.mutations.fields.workHoursFromHome',
  primary_evaluator: 'workforce.people.fields.primaryEvaluator',
  secondary_evaluator: 'workforce.people.fields.secondaryEvaluator',
  mentor: 'workforce.people.fields.mentor',
  career_start_date: 'workforce.people.fields.careerStartDate',
  last_day: 'workforce.people.deactivation.lastDayLabel',
}

const normalizeInflowSetup = (
  config: InflowPageSetupConfig | null | undefined,
  t: TFunction,
): InflowPageSetupConfig => {
  const configuredColumns = config?.columns || []
  const configuredByKey = new Map(configuredColumns.map((column) => [column.key, column]))

  return {
    columns: INFLOW_COLUMN_KEYS.map((key) => {
      const current = configuredByKey.get(key)
      const defaultLabelKey = INFLOW_COLUMN_LABEL_KEYS[key]
      const defaultLabel = t(defaultLabelKey)
      const locked = key === 'name' || key === 'actions'
      return {
        key,
        label: current?.label || defaultLabel,
        visible: locked ? true : current?.visible ?? true,
        locked,
      }
    }),
  }
}

const normalizeAddEmployeeSetup = (
  config: AddEmployeePageSetupConfig | null | undefined,
  t: TFunction,
): AddEmployeePageSetupConfig => {
  const configuredFields = config?.fields || []
  const configuredByKey = new Map(configuredFields.map((field) => [field.key, field]))

  return {
    fields: ADD_EMPLOYEE_FIELD_ORDER.map((key) => {
      const current = configuredByKey.get(key)
      const locked = key === 'first_name' || key === 'last_name' || key === 'work_email'
      const section = current?.section || (ADD_EMPLOYEE_FIELD_ORDER.indexOf(key) < 19 ? 'personal' : 'work')
      return {
        key,
        label: t(ADD_EMPLOYEE_FIELD_LABEL_KEYS[key] || key),
        section,
        visible: locked ? true : current?.visible ?? true,
        required: locked ? true : current?.required ?? false,
        locked,
      }
    }),
  }
}

const MODULE_SUBTABS = [
  { key: 'access', label: 'settings.modules.subtabs.access' },
  { key: 'setup', label: 'settings.modules.subtabs.setup' },
] as const

const PLANNING_TABS: TabNode[] = [
  {
    key: 'attendance-calendar',
    label: 'settings.modules.tabs.attendanceCalendar',
    children: [
      { key: 'weekly-schedule', label: 'settings.modules.tabs.weeklySchedule' },
      { key: 'submit-request', label: 'settings.modules.tabs.submitRequest' },
    ],
  },
  { key: 'past-requests', label: 'settings.modules.tabs.pastRequests' },
  { key: 'attendance', label: 'settings.modules.tabs.attendance' },
  {
    key: 'leave-management',
    label: 'settings.modules.tabs.leaveManagement',
    children: [
      { key: 'leave-requests', label: 'settings.modules.tabs.leaveRequests' },
      { key: 'leave-balances', label: 'settings.modules.tabs.leaveBalances' },
      { key: 'public-holidays', label: 'settings.modules.tabs.publicHolidays' },
    ],
  },
  {
    key: 'workspace',
    label: 'settings.modules.tabs.workspace',
    children: [
      { key: 'desks', label: 'settings.modules.tabs.desks' },
      { key: 'rooms', label: 'settings.modules.tabs.rooms' },
      { key: 'offices', label: 'settings.modules.tabs.offices' },
    ],
  },
  {
    key: 'time-tracking',
    label: 'settings.modules.tabs.timeTracking',
    children: [
      { key: 'projects', label: 'settings.modules.tabs.projects' },
      { key: 'time-entries', label: 'settings.modules.tabs.timeEntries' },
      { key: 'activities', label: 'settings.modules.tabs.activities' },
    ],
  },
]

const WORKFORCE_TABS: TabNode[] = [
  { key: 'people', label: 'workforce.tabs.people' },
  { key: 'teams', label: 'workforce.tabs.teams' },
  { key: 'organization', label: 'workforce.tabs.organization' },
  { key: 'assets', label: 'workforce.tabs.assets' },
  { key: 'mutations', label: 'workforce.tabs.mutations' },
]

const RECRUITMENT_TABS: TabNode[] = [
  { key: 'candidates', label: 'recruitment.tabs.candidates' },
  { key: 'pipeline', label: 'recruitment.tabs.pipeline' },
  { key: 'interviews', label: 'recruitment.tabs.interviews' },
  { key: 'reports', label: 'recruitment.tabs.reports' },
  { key: 'vacancy', label: 'recruitment.tabs.vacancy' },
  { key: 'overview', label: 'recruitment.tabs.overview' },
  { key: 'inflow', label: 'recruitment.tabs.inflow' },
]

const EXPLORE_TABS: TabNode[] = [
  { key: 'explore', label: 'explore.tabs.explore' },
  { key: 'paths', label: 'explore.tabs.paths' },
  { key: 'library', label: 'explore.tabs.library', disabledRoles: ['user'] },
  { key: 'statistics', label: 'explore.tabs.statistics', disabledRoles: ['user'] },
]

const ACCESS_SECTIONS: ModuleSection[] = [
  {
    key: 'planning',
    titleKey: 'settings.modules.planningSection',
    descriptionKey: 'settings.modules.planningSectionDescription',
    tabs: PLANNING_TABS,
  },
  {
    key: 'workforce',
    titleKey: 'settings.modules.workforceSection',
    descriptionKey: 'settings.modules.workforceSectionDescription',
    tabs: WORKFORCE_TABS,
  },
  {
    key: 'recruitment',
    titleKey: 'settings.modules.recruitmentSection',
    descriptionKey: 'settings.modules.recruitmentSectionDescription',
    tabs: RECRUITMENT_TABS,
  },
  {
    key: 'explore',
    titleKey: 'settings.modules.exploreSection',
    descriptionKey: 'settings.modules.exploreSectionDescription',
    tabs: EXPLORE_TABS,
  }
]

const CHILD_TO_PARENT: Record<string, string> = {}
const PARENT_TO_CHILDREN: Record<string, string[]> = {}
for (const tab of PLANNING_TABS) {
  if (tab.children) {
    PARENT_TO_CHILDREN[tab.key] = tab.children.map((c) => c.key)
    for (const child of tab.children) {
      CHILD_TO_PARENT[child.key] = tab.key
    }
  }
}

const ALL_ROLES = [
  { id: 'finance', name: 'settings.roles.finance' },
  { id: 'country_admin', name: 'settings.roles.countryAdmin' },
  { id: 'team_leader', name: 'settings.roles.teamLeader' },
  { id: 'user', name: 'settings.roles.user' },
]

const ALL_COUNTRIES_OPTION = { key: '*', name: 'settings.modules.allCountries', code: '' }

const COUNTRY_OPTIONS = [
  ALL_COUNTRIES_OPTION,
  ...phoneNumbers.map((c) => ({ key: c.code.toUpperCase(), name: c.name, code: c.code.toLowerCase() })),
]

interface TabConfig {
  roles: string[]
  countries: string[]
}

const ModulesBar = ({
  selectedTab,
  onSelectTab,
  t,
}: {
  selectedTab: string
  onSelectTab: (tab: string) => void
  t: (key: string) => string
}) => (
  <Box width="100%" borderBottom="1px solid" borderColor="solid.50">
    <HStack spacing="24px" align="stretch" px={5}>
      {MODULE_SUBTABS.map((tab) => (
        <HStack
          key={tab.key}
          spacing="12px"
          py="18px"
          px="16px"
          height="56px"
          _hover={{ backgroundColor: '#D0DDFF4D' }}
          cursor="pointer"
          color="solid.900"
          onClick={() => onSelectTab(tab.key)}
          borderBottomColor={selectedTab === tab.key ? 'primary.100' : 'transparent'}
          borderBottomWidth={selectedTab === tab.key ? '2px' : '0px'}
        >
          <Text textStyle="l-s">{t(tab.label)}</Text>
        </HStack>
      ))}
    </HStack>
  </Box>
)

const RolePills = ({
  roles,
  onToggle,
  disabledRoles = [],
  t,
}: {
  roles: string[]
  onToggle: (roleId: string) => void
  disabledRoles?: string[]
  t: (key: string) => string
}) => (
  <HStack spacing="6px" flexWrap="wrap" align="center">
    {ALL_ROLES.filter((role) => !disabledRoles.includes(role.id)).map((role) => {
      const selected = roles.includes(role.id)
      return (
        <Box
          key={role.id}
          as="button"
          type="button"
          py="4px"
          px="10px"
          minH="28px"
          borderRadius="999px"
          border="1px solid"
          borderColor={selected ? 'primary.100' : 'solid.100'}
          bg={selected ? 'primary.0' : 'white'}
          color={selected ? 'primary.200' : 'solid.600'}
          fontWeight={selected ? '600' : '500'}
          fontSize="11px"
          lineHeight="1"
          transition="all 0.15s ease"
          _hover={{ borderColor: 'primary.100', bg: 'primary.0' }}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            onToggle(role.id)
          }}
        >
          {t(role.name)}
        </Box>
      )
    })}
  </HStack>
)

const CountrySelect = ({
  countries,
  onChange,
  t,
}: {
  countries: string[]
  onChange: (selected: { key: string; name: string }[]) => void
  t: (key: string) => string
}) => {
  const selectedValues = countries.map((code) => {
    if (code === '*') return { ...ALL_COUNTRIES_OPTION, name: t(ALL_COUNTRIES_OPTION.name) }
    return { key: code, name: getCountryNameByCode(code) || code, code: code.toLowerCase() }
  })

  const translatedOptions = COUNTRY_OPTIONS.map((opt) =>
    opt.key === '*' ? { ...opt, name: t(opt.name) } : opt
  )

  return (
    <Box onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
      <ModalMultiSelect
        label={t('common.fields.countries')}
        hideLabel
        compact
        options={translatedOptions}
        selectedValues={selectedValues}
        onChange={onChange}
        showCountryFlag
        placeholder={t('settings.modules.selectCountriesPlaceholder')}
      />
    </Box>
  )
}

const TabConfigRow = ({
  tab,
  config,
  onToggleRole,
  onCountryChange,
  getConfig,
  isChild,
  disabledRoles = [],
  t,
}: {
  tab: TabNode
  config: TabConfig
  onToggleRole: (tabKey: string, roleId: string) => void
  onCountryChange: (tabKey: string, selected: { key: string; name: string }[]) => void
  getConfig: (key: string) => TabConfig
  isChild?: boolean
  disabledRoles?: string[]
  t: (key: string) => string
}) => {
  const [childrenExpanded, setChildrenExpanded] = useState(false)
  const hasChildren = tab.children && tab.children.length > 0
  const hasRoles = config.roles.length > 0
  const isConfigured = config.roles.length > 0

  const handleHeaderClick = () => {
    if (hasChildren) setChildrenExpanded(!childrenExpanded)
  }

  return (
    <>
      <Box
        px="20px"
        py="12px"
        pl={isChild ? '36px' : '20px'}
        borderTop={isChild ? '1px solid' : 'none'}
        borderColor="solid.50"
        bg={isChild ? 'white' : 'solid.0'}
        cursor={hasChildren ? 'pointer' : 'default'}
        onClick={(e: MouseEvent<HTMLDivElement>) => {
          e.preventDefault()
          handleHeaderClick()
        }}
        _hover={hasChildren ? { bg: isChild ? 'solid.0' : 'solid.25' } : {}}
        transition="background 0.15s ease"
      >
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align={{ base: 'stretch', lg: 'center' }}
          gap={{ base: '10px', lg: '16px' }}
        >
          <Flex
            minW={{ lg: isChild ? '180px' : '220px' }}
            flex={{ lg: '0 0 220px' }}
            align="center"
            gap="8px"
          >
            {hasChildren && (
              <Flex align="center" justify="center" w="18px" h="18px" borderRadius="6px" flexShrink={0}>
                <Box as={childrenExpanded ? ChevronDown : ChevronRight} size={14} color="solid.400" />
              </Flex>
            )}
            <Text
              fontSize={isChild ? '13px' : '14px'}
              fontWeight={isChild ? '500' : '600'}
              color={isChild ? 'solid.700' : 'solid.900'}
              lineHeight="1.4"
            >
              {t(tab.label)}
            </Text>
            <Badge
              px="6px"
              py="2px"
              borderRadius="999px"
              bg={isConfigured ? 'primary.0' : 'solid.25'}
              color={isConfigured ? 'primary.200' : 'solid.500'}
              textTransform="none"
              fontSize="10px"
              ml="auto"
            >
              {config.roles.length}
            </Badge>
          </Flex>

          <Box flex="1" onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <RolePills roles={config.roles} onToggle={(roleId) => onToggleRole(tab.key, roleId)} disabledRoles={disabledRoles} t={t} />
          </Box>

          <Box
            minW={{ lg: '230px' }}
            maxW={{ lg: '230px' }}
            onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {hasRoles ? (
              <CountrySelect
                countries={config.countries}
                onChange={(selected) => onCountryChange(tab.key, selected)}
                t={t}
              />
            ) : (
              <Text fontSize="12px" color="solid.400">
                {t('settings.modules.countryPlaceholder')}
              </Text>
            )}
          </Box>
        </Flex>
      </Box>

      {hasChildren && (
        <Collapse in={childrenExpanded}>
          <Box
            ml="20px"
            mr="20px"
            mb="12px"
            borderLeft="2px solid"
            borderColor="solid.50"
            bg="white"
            overflow="hidden"
          >
            {tab.children!.map((child, idx) => (
              <Box key={child.key}>
                {idx > 0 && <Box h="1px" bg="solid.50" ml="16px" />}
                <TabConfigRow
                  tab={child}
                  config={getConfig(child.key)}
                  onToggleRole={onToggleRole}
                  onCountryChange={onCountryChange}
                  getConfig={getConfig}
                  isChild
                  disabledRoles={disabledRoles}
                  t={t}
                />
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </>
  )
}

const InflowSetupEditor = ({
  columns,
  onToggleVisible,
  onLabelChange,
  onMove,
  isSaving,
  isDefault,
  onSave,
  t,
}: {
  columns: InflowPageSetupConfig['columns']
  onToggleVisible: (key: string) => void
  onLabelChange: (key: string, value: string) => void
  onMove: (key: string, direction: 'up' | 'down') => void
  isSaving: boolean
  isDefault: boolean
  onSave: () => void
  t: TFunction
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const visibleCount = columns.filter((column) => column.visible).length

  return (
    <Box borderRadius="18px" border="1px solid" borderColor="solid.50" bg="white" overflow="hidden">
      <Flex
        px="20px"
        py="18px"
        align={{ base: 'flex-start', lg: 'center' }}
        justify="space-between"
        gap="16px"
        bg="linear-gradient(180deg, rgba(245,247,250,0.75) 0%, rgba(255,255,255,1) 100%)"
      >
        <HStack align="flex-start" spacing="14px" flex="1" minW={0}>
          <Flex w="42px" h="42px" borderRadius="14px" bg="primary.0" color="primary.200" align="center" justify="center" flexShrink={0}>
            <Box as={TableProperties} size={18} />
          </Flex>
          <Box minW={0}>
            <HStack spacing="10px" mb="6px" flexWrap="wrap">
              <Text fontSize="15px" fontWeight="700" color="solid.900">
                {t('settings.modules.inflowSetup.title')}
              </Text>
              {isDefault && (
                <Badge px="8px" py="4px" borderRadius="999px" bg="warning.0" color="warning.100" textTransform="none">
                  {t('settings.modules.inflowSetup.usingDefault')}
                </Badge>
              )}
            </HStack>
            <Text fontSize="13px" color="solid.600" lineHeight="1.6" mb="10px">
              {t('settings.modules.inflowSetup.description')}
            </Text>
            <HStack spacing="8px" flexWrap="wrap">
              <Badge px="8px" py="4px" borderRadius="999px" bg="primary.0" color="primary.200" textTransform="none">
                {t('settings.modules.summary.visibleCount', { count: visibleCount })}
              </Badge>
              <Badge px="8px" py="4px" borderRadius="999px" bg="solid.25" color="solid.700" textTransform="none">
                {t('settings.modules.summary.totalCount', { count: columns.length })}
              </Badge>
            </HStack>
          </Box>
        </HStack>

        <HStack spacing="8px" alignSelf={{ base: 'stretch', lg: 'center' }}>
          <Button variant="white" size="sm" onClick={() => setIsOpen((prev) => !prev)}>
            {isOpen ? t('common.actions.hide') : t('common.actions.show')}
          </Button>
          <Button variant="primary" size="sm" onClick={onSave} isLoading={isSaving}>
            {t('common.actions.save')}
          </Button>
        </HStack>
      </Flex>

      <Collapse in={isOpen}>
        <Box px="20px" py="10px" borderTop="1px solid" borderColor="solid.50" bg="solid.0">
          <Text fontSize="11px" color="solid.500" textTransform="uppercase" letterSpacing="0.7px" fontWeight="700">
            {t('settings.modules.inflowSetup.helper')}
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px" px="20px" py="16px">
          {columns.map((column, index) => {
            const translatedDefault = t(INFLOW_COLUMN_LABEL_KEYS[column.key] || column.label)
            return (
              <Box
                key={column.key}
                border="1px solid"
                borderColor={column.visible ? 'solid.100' : 'solid.50'}
                bg={column.visible ? 'white' : 'solid.0'}
                borderRadius="14px"
                p="14px"
              >
                <Flex align="flex-start" justify="space-between" gap="12px" mb="12px">
                  <Box minW={0}>
                    <HStack spacing="8px" mb="4px" flexWrap="wrap">
                      <Text fontSize="13px" fontWeight="700" color="solid.900">
                        {translatedDefault}
                      </Text>
                      {column.locked && (
                        <Badge px="6px" py="2px" borderRadius="999px" bg="solid.25" color="solid.600" textTransform="none">
                          {t('settings.modules.inflowSetup.locked')}
                        </Badge>
                      )}
                    </HStack>
                  </Box>

                  <HStack spacing="6px" flexShrink={0}>
                    <IconButton
                      aria-label={t('common.actions.previous')}
                      icon={<ArrowUp size={14} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => onMove(column.key, 'up')}
                      isDisabled={index === 0}
                    />
                    <IconButton
                      aria-label={t('common.actions.next')}
                      icon={<ArrowDown size={14} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => onMove(column.key, 'down')}
                      isDisabled={index === columns.length - 1}
                    />
                  </HStack>
                </Flex>

                <Box mb="12px">
                  <Text fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.7px" mb="6px">
                    {t('settings.modules.inflowSetup.customLabel')}
                  </Text>
                  <Input
                    size="sm"
                    value={column.label}
                    onChange={(e) => onLabelChange(column.key, e.target.value)}
                    placeholder={translatedDefault}
                    isDisabled={column.locked && (column.key === 'name' || column.key === 'actions')}
                  />
                </Box>

                <Flex align="center" justify="space-between" gap="8px">
                  <Text fontSize="12px" color="solid.600">
                    {column.visible ? t('settings.modules.inflowSetup.visibleDescription') : t('settings.modules.inflowSetup.hiddenDescription')}
                  </Text>
                    <HStack spacing="8px">
                      <Switch
                        isChecked={column.visible}
                        onChange={() => onToggleVisible(column.key)}
                        isDisabled={column.locked}
                        colorScheme="primary"
                      />
                      <Text fontSize="12px" color={column.visible ? 'solid.700' : 'solid.400'} minW="44px">
                        {column.visible ? t('common.labels.active') : t('common.labels.disabled')}
                      </Text>
                    </HStack>
                  </Flex>
                </Box>
            )
          })}
        </SimpleGrid>
      </Collapse>
    </Box>
  )
}

const AddEmployeeSetupEditor = ({
  fields,
  onToggleVisible,
  onToggleRequired,
  isSaving,
  isDefault,
  onSave,
  t,
}: {
  fields: PageSetupFieldConfig[]
  onToggleVisible: (key: string) => void
  onToggleRequired: (key: string) => void
  isSaving: boolean
  isDefault: boolean
  onSave: () => void
  t: TFunction
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<'personal' | 'work', boolean>>({
    personal: true,
    work: false,
  })
  const sections: Array<{ key: 'personal' | 'work'; title: string }> = [
    { key: 'personal', title: t('workforce.people.sections.personalInformation') },
    { key: 'work', title: t('workforce.people.sections.workInformation') },
  ]
  const visibleCount = fields.filter((field) => field.visible).length
  const requiredCount = fields.filter((field) => field.visible && field.required).length

  return (
    <Box borderRadius="18px" border="1px solid" borderColor="solid.50" bg="white" overflow="hidden">
      <Flex
        px="20px"
        py="18px"
        align={{ base: 'flex-start', lg: 'center' }}
        justify="space-between"
        gap="16px"
        bg="linear-gradient(180deg, rgba(245,247,250,0.75) 0%, rgba(255,255,255,1) 100%)"
      >
        <HStack align="flex-start" spacing="14px" flex="1" minW={0}>
          <Flex w="42px" h="42px" borderRadius="14px" bg="primary.0" color="primary.200" align="center" justify="center" flexShrink={0}>
            <Box as={UserRoundPen} size={18} />
          </Flex>
          <Box minW={0}>
            <HStack spacing="10px" mb="6px" flexWrap="wrap">
              <Text fontSize="15px" fontWeight="700" color="solid.900">
                {t('settings.modules.employeeSetup.title')}
              </Text>
              {isDefault && (
                <Badge px="8px" py="4px" borderRadius="999px" bg="warning.0" color="warning.100" textTransform="none">
                  {t('settings.modules.employeeSetup.usingDefault')}
                </Badge>
              )}
            </HStack>
            <Text fontSize="13px" color="solid.600" lineHeight="1.6" mb="10px">
              {t('settings.modules.employeeSetup.description')}
            </Text>
            <HStack spacing="8px" flexWrap="wrap">
              <Badge px="8px" py="4px" borderRadius="999px" bg="primary.0" color="primary.200" textTransform="none">
                {t('settings.modules.summary.visibleCount', { count: visibleCount })}
              </Badge>
              <Badge px="8px" py="4px" borderRadius="999px" bg="success.0" color="success.200" textTransform="none">
                {t('settings.modules.summary.requiredCount', { count: requiredCount })}
              </Badge>
              <Badge px="8px" py="4px" borderRadius="999px" bg="solid.25" color="solid.700" textTransform="none">
                {t('settings.modules.summary.totalCount', { count: fields.length })}
              </Badge>
            </HStack>
          </Box>
        </HStack>
        <Button variant="primary" size="sm" onClick={onSave} isLoading={isSaving}>
          {t('common.actions.save')}
        </Button>
      </Flex>

      <Box px="20px" py="10px" borderTop="1px solid" borderColor="solid.50" bg="solid.0">
        <Text fontSize="11px" color="solid.500" textTransform="uppercase" letterSpacing="0.7px" fontWeight="700">
          {t('settings.modules.employeeSetup.helper')}
        </Text>
      </Box>

      <VStack align="stretch" spacing="12px" px="20px" py="16px">
        {sections.map((section) => {
          const sectionFields = fields.filter((field) => field.section === section.key)
          const visibleInSection = sectionFields.filter((field) => field.visible).length
          const requiredInSection = sectionFields.filter((field) => field.visible && field.required).length
          const isExpanded = expandedSections[section.key]
          return (
            <Box key={section.key} border="1px solid" borderColor="solid.50" borderRadius="16px" overflow="hidden">
              <Flex
                px="18px"
                py="16px"
                align={{ base: 'flex-start', lg: 'center' }}
                justify="space-between"
                gap="12px"
                cursor="pointer"
                onClick={() => setExpandedSections((prev) => ({ ...prev, [section.key]: !prev[section.key] }))}
                _hover={{ bg: 'solid.0' }}
              >
                <Box>
                  <HStack spacing="10px" mb="6px" flexWrap="wrap">
                    <Text fontSize="14px" fontWeight="700" color="solid.900">
                      {section.title}
                    </Text>
                    <Badge px="8px" py="4px" borderRadius="999px" bg="primary.0" color="primary.200" textTransform="none">
                      {t('settings.modules.summary.visibleOfTotal', { visible: visibleInSection, total: sectionFields.length })}
                    </Badge>
                    <Badge px="8px" py="4px" borderRadius="999px" bg="solid.25" color="solid.700" textTransform="none">
                      {t('settings.modules.summary.requiredCount', { count: requiredInSection })}
                    </Badge>
                  </HStack>
                  <Text fontSize="12px" color="solid.600">
                    {t(section.key === 'personal' ? 'settings.modules.employeeSetup.personalDescription' : 'settings.modules.employeeSetup.workDescription')}
                  </Text>
                </Box>
                <Flex align="center" justify="center" w="28px" h="28px" borderRadius="10px" bg="solid.0" color="solid.500" flexShrink={0}>
                  <Box as={isExpanded ? ChevronDown : ChevronRight} size={16} />
                </Flex>
              </Flex>

              <Collapse in={isExpanded}>
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px" px="18px" pb="18px">
                  {sectionFields.map((field) => (
                    <Box
                      key={field.key}
                      border="1px solid"
                      borderColor={field.visible ? 'solid.100' : 'solid.50'}
                      bg={field.visible ? 'white' : 'solid.0'}
                      borderRadius="14px"
                      p="14px"
                    >
                      <HStack spacing="8px" align="flex-start" mb="8px" flexWrap="wrap">
                        <Text fontSize="13px" fontWeight="700" color="solid.900">
                          {field.label}
                        </Text>
                        {field.locked && (
                          <Badge px="6px" py="2px" borderRadius="999px" bg="solid.25" color="solid.600" textTransform="none">
                            {t('settings.modules.employeeSetup.locked')}
                          </Badge>
                        )}
                      </HStack>

                      <Flex align="center" justify="space-between" gap="12px" mb="10px">
                        <Box>
                          <Text fontSize="12px" fontWeight="600" color="solid.700">
                            {t('settings.modules.employeeSetup.visibility')}
                          </Text>
                          <Text fontSize="11px" color="solid.500">
                            {field.visible
                              ? t('settings.modules.employeeSetup.visibleHelp')
                              : t('settings.modules.employeeSetup.hiddenHelp')}
                          </Text>
                        </Box>
                        <Switch
                          isChecked={field.visible}
                          onChange={() => onToggleVisible(field.key)}
                          isDisabled={field.locked}
                          colorScheme="primary"
                        />
                      </Flex>

                      <Flex align="center" justify="space-between" gap="12px">
                        <Box>
                          <Text fontSize="12px" fontWeight="600" color="solid.700">
                            {t('settings.modules.employeeSetup.required')}
                          </Text>
                          <Text fontSize="11px" color="solid.500">
                            {field.visible
                              ? t('settings.modules.employeeSetup.requiredHelp')
                              : t('settings.modules.employeeSetup.disabledUntilVisible')}
                          </Text>
                        </Box>
                        <Switch
                          isChecked={field.required}
                          onChange={() => onToggleRequired(field.key)}
                          isDisabled={field.locked || !field.visible}
                          colorScheme="primary"
                        />
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>
              </Collapse>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}

const ModulesSettings = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selectedSubtab, setSelectedSubtab] = useState('access')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    planning: true,
    workforce: true,
    recruitment: true,
  })
  const [tabConfigs, setTabConfigs] = useState<Record<string, Record<string, TabConfig>>>({})
  const [inflowSetup, setInflowSetup] = useState<InflowPageSetupConfig | null>(null)
  const [addEmployeeSetup, setAddEmployeeSetup] = useState<AddEmployeePageSetupConfig | null>(null)

  const { data: permissions } = useQuery({
    queryKey: ['page-permissions'],
    queryFn: getPagePermissions,
  })

  const { data: inflowSetupResponse } = useQuery({
    queryKey: ['page-setup', 'inflow'],
    queryFn: getInflowPageSetup,
  })

  const { data: addEmployeeSetupResponse } = useQuery({
    queryKey: ['page-setup', 'add-employee'],
    queryFn: getAddEmployeePageSetup,
  })

  useEffect(() => {
    if (!permissions) return
    const configs: Record<string, Record<string, TabConfig>> = {}
    for (const perm of permissions) {
      if (!configs[perm.section]) configs[perm.section] = {}
      configs[perm.section][perm.tab] = { roles: perm.roles, countries: perm.countries }
    }
    setTabConfigs(configs)
  }, [permissions])

  useEffect(() => {
    if (!inflowSetupResponse?.config) return
    setInflowSetup(normalizeInflowSetup(inflowSetupResponse.config, t))
  }, [inflowSetupResponse, t])

  useEffect(() => {
    if (!addEmployeeSetupResponse?.config) return
    setAddEmployeeSetup(normalizeAddEmployeeSetup(addEmployeeSetupResponse.config, t))
  }, [addEmployeeSetupResponse, t])

  const saveMutation = useMutation({
    mutationFn: (section: string) => {
      const tabs = Object.entries(tabConfigs[section] || {})
        .filter(([, config]) => config.roles.length > 0)
        .map(([tab, config]) => ({ tab, roles: config.roles, countries: config.countries }))

      return updatePagePermissions({ section, tabs })
    },
    onSuccess: () => {
      handleToast(toast, true, t('settings.modules.permissionsSaved'), t('settings.modules.permissionsUpdated'))
      queryClient.invalidateQueries({ queryKey: ['page-permissions'] })
      queryClient.invalidateQueries({ queryKey: ['my-permissions'] })
    },
    onError: () => {
      handleToast(toast, false, t('common.states.error'), t('settings.modules.savePermissionsError'))
    },
  })

  const saveInflowSetupMutation = useMutation({
    mutationFn: (config: InflowPageSetupConfig) => updateInflowPageSetup(config),
    onSuccess: () => {
      handleToast(
        toast,
        true,
        t('settings.modules.inflowSetup.saved'),
        t('settings.modules.inflowSetup.updated'),
      )
      queryClient.invalidateQueries({ queryKey: ['page-setup', 'inflow'] })
      queryClient.invalidateQueries({ queryKey: ['inflow'] })
    },
    onError: () => {
      handleToast(
        toast,
        false,
        t('common.states.error'),
        t('settings.modules.inflowSetup.saveError'),
      )
    },
  })

  const saveAddEmployeeSetupMutation = useMutation({
    mutationFn: (config: AddEmployeePageSetupConfig) => updateAddEmployeePageSetup(config),
    onSuccess: () => {
      handleToast(
        toast,
        true,
        t('settings.modules.employeeSetup.saved'),
        t('settings.modules.employeeSetup.updated'),
      )
      queryClient.invalidateQueries({ queryKey: ['page-setup', 'add-employee'] })
    },
    onError: () => {
      handleToast(
        toast,
        false,
        t('common.states.error'),
        t('settings.modules.employeeSetup.saveError'),
      )
    },
  })

  const toggleRole = (sectionKey: string, tabKey: string, roleId: string) => {
    setTabConfigs((prev) => {
      const next = { ...prev }
      const sectionConfigs = { ...(next[sectionKey] || {}) }
      next[sectionKey] = sectionConfigs
      const current = sectionConfigs[tabKey] || { roles: [], countries: [] }
      const wasSelected = current.roles.includes(roleId)
      const updated = wasSelected ? current.roles.filter((r) => r !== roleId) : [...current.roles, roleId]
      const countries =
        !wasSelected && current.roles.length === 0 && current.countries.length === 0 ? ['*'] : current.countries
      sectionConfigs[tabKey] = { roles: updated, countries }

      if (sectionKey === 'planning' && !wasSelected) {
        const parentKey = CHILD_TO_PARENT[tabKey]
        if (parentKey) {
          const parentConfig = sectionConfigs[parentKey] || { roles: [], countries: [] }
          if (!parentConfig.roles.includes(roleId)) {
            const parentCountries =
              parentConfig.roles.length === 0 && parentConfig.countries.length === 0 ? ['*'] : parentConfig.countries
            sectionConfigs[parentKey] = {
              roles: [...parentConfig.roles, roleId],
              countries: parentCountries,
            }
          }
        }
      } else if (sectionKey === 'planning') {
        const childKeys = PARENT_TO_CHILDREN[tabKey]
        if (childKeys && updated.length === 0) {
          for (const childKey of childKeys) {
            sectionConfigs[childKey] = { roles: [], countries: [] }
          }
        } else if (childKeys) {
          for (const childKey of childKeys) {
            const childConfig = sectionConfigs[childKey] || { roles: [], countries: [] }
            if (childConfig.roles.includes(roleId)) {
              const childUpdated = childConfig.roles.filter((r) => r !== roleId)
              sectionConfigs[childKey] = { roles: childUpdated, countries: childUpdated.length > 0 ? childConfig.countries : [] }
            }
          }
        }
      }

      return next
    })
  }

  const handleCountryChange = (sectionKey: string, tabKey: string, selectedOptions: { key: string; name: string }[]) => {
    const keys = selectedOptions.map((o) => o.key)
    const currentCountries = tabConfigs[sectionKey]?.[tabKey]?.countries || []

    let newCountries: string[]
    if (keys.includes('*') && !currentCountries.includes('*')) {
      newCountries = ['*']
    } else if (keys.includes('*') && keys.length > 1) {
      newCountries = keys.filter((k) => k !== '*')
    } else {
      newCountries = keys
    }

    setTabConfigs((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        [tabKey]: {
          roles: prev[sectionKey]?.[tabKey]?.roles || [],
          countries: newCountries,
        },
      },
    }))
  }

  const getConfig = (sectionKey: string, key: string): TabConfig => tabConfigs[sectionKey]?.[key] || { roles: [], countries: [] }

  const getConfiguredCount = (sectionKey: string) =>
    Object.values(tabConfigs[sectionKey] || {}).filter((c) => c.roles.length > 0).length

  const moveInflowColumn = (key: string, direction: 'up' | 'down') => {
    setInflowSetup((prev) => {
      if (!prev) return prev
      const columns = [...prev.columns]
      const index = columns.findIndex((column) => column.key === key)
      if (index === -1) return prev

      const nextIndex = direction === 'up' ? index - 1 : index + 1
      if (nextIndex < 0 || nextIndex >= columns.length) return prev

      const [moved] = columns.splice(index, 1)
      columns.splice(nextIndex, 0, moved)
      return { ...prev, columns }
    })
  }

  const updateInflowColumn = (
    key: string,
    updater: (column: InflowPageSetupConfig['columns'][number]) => InflowPageSetupConfig['columns'][number],
  ) => {
    setInflowSetup((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        columns: prev.columns.map((column) => (column.key === key ? updater(column) : column)),
      }
    })
  }

  const updateAddEmployeeField = (
    key: string,
    updater: (field: PageSetupFieldConfig) => PageSetupFieldConfig,
  ) => {
    setAddEmployeeSetup((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        fields: prev.fields.map((field) => (field.key === key ? updater(field) : field)),
      }
    })
  }

  return (
    <Box width="100%">
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid"
        borderColor="solid.50"
        overflow="hidden"
        boxShadow="0 1px 3px rgba(0,0,0,0.02)"
      >
        <ModulesBar selectedTab={selectedSubtab} onSelectTab={setSelectedSubtab} t={t} />

        {selectedSubtab === 'access' ? (
          <VStack gap={4} width="100%" align="stretch" p="20px">
            <Box
              borderRadius="14px"
              border="1px solid"
              borderColor="solid.50"
              bg="solid.0"
              p="16px 18px"
            >
              <HStack align="flex-start" spacing="10px">
                <Flex
                  w="34px"
                  h="34px"
                  borderRadius="10px"
                  bg="primary.0"
                  color="primary.200"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Box as={Settings2} size={18} />
                </Flex>
                <Box>
                  <Text fontSize="15px" fontWeight="700" color="solid.900" mb="4px">
                    {t('settings.modules.accessIntro.title')}
                  </Text>
                  <Text fontSize="12px" color="solid.600" lineHeight="1.5">
                    {t('settings.modules.accessIntro.description')}
                  </Text>
                </Box>
              </HStack>
            </Box>

            {ACCESS_SECTIONS.map((section) => {
              const isExpanded = expandedSections[section.key] ?? true
              const configuredCount = getConfiguredCount(section.key)

              return (
                <Box
                  key={section.key}
                  bg="white"
                  borderRadius="16px"
                  border="1px solid"
                  borderColor="solid.50"
                  overflow="hidden"
                >
                  <Flex
                    px="20px"
                    py="14px"
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={(e: MouseEvent<HTMLDivElement>) => {
                      e.preventDefault()
                      setExpandedSections((prev) => ({ ...prev, [section.key]: !isExpanded }))
                    }}
                    _hover={{ bg: 'solid.0' }}
                    transition="background 0.15s ease"
                    gap="16px"
                  >
                    <HStack gap="12px" minW={0} flex="1">
                      <Flex align="center" justify="center" w="20px" h="20px">
                        <Box as={isExpanded ? ChevronDown : ChevronRight} size={16} color="solid.400" />
                      </Flex>
                      <Box minW={0}>
                        <Text fontSize="12px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.8px">
                          {t(section.titleKey)}
                        </Text>
                        <Text fontSize="12px" color="solid.600" mt="2px">
                          {t(section.descriptionKey)}
                        </Text>
                      </Box>
                      <Badge
                        px="8px"
                        py="4px"
                        borderRadius="999px"
                        bg={configuredCount > 0 ? 'primary.0' : 'solid.25'}
                        color={configuredCount > 0 ? 'primary.200' : 'solid.500'}
                        textTransform="none"
                        fontSize="11px"
                      >
                        {t('settings.modules.configuredCount', { count: configuredCount })}
                      </Badge>
                    </HStack>
                    <Button
                      variant="primary"
                      size="sm"
                      h="34px"
                      px="20px"
                      borderRadius="8px"
                      fontSize="13px"
                      fontWeight="600"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        saveMutation.mutate(section.key)
                      }}
                      isLoading={saveMutation.isPending}
                    >
                      {t('common.actions.save')}
                    </Button>
                  </Flex>

                  <Collapse in={isExpanded}>
                    <Box borderTop="1px solid" borderColor="solid.50">
                      <Flex
                        display={{ base: 'none', lg: 'flex' }}
                        px="20px"
                        py="10px"
                        bg="solid.0"
                        borderBottom="1px solid"
                        borderColor="solid.50"
                        align="center"
                        gap="16px"
                      >
                        <Text flex="0 0 220px" fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.7px">
                          {t('settings.modules.columns.page')}
                        </Text>
                        <Text flex="1" fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.7px">
                          {t('settings.modules.columns.roles')}
                        </Text>
                        <Text flex="0 0 230px" fontSize="11px" fontWeight="700" color="solid.500" textTransform="uppercase" letterSpacing="0.7px">
                          {t('settings.modules.columns.countries')}
                        </Text>
                      </Flex>
                      {section.tabs.map((tab, idx) => (
                        <Box key={tab.key}>
                          {idx > 0 && <Box h="1px" bg="solid.50" />}
                          <TabConfigRow
                            tab={tab}
                            config={getConfig(section.key, tab.key)}
                            onToggleRole={(tabKey, roleId) => toggleRole(section.key, tabKey, roleId)}
                            onCountryChange={(tabKey, selected) => handleCountryChange(section.key, tabKey, selected)}
                            getConfig={(key) => getConfig(section.key, key)}
                            disabledRoles={[
                              ...(
                                section.key === 'workforce' ||
                                section.key === 'recruitment' ||
                                (section.key === 'planning' && tab.key === 'leave-management')
                                  ? ['user']
                                  : []
                              ),
                              ...(tab.disabledRoles ?? []),
                            ]}
                            t={t}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )
            })}
          </VStack>
        ) : (
          <VStack gap={6} width="100%" align="stretch" p="24px">
            <Box
              borderRadius="16px"
              border="1px solid"
              borderColor="solid.50"
              bg="solid.0"
              p="20px"
            >
              <Text fontSize="16px" fontWeight="700" color="solid.900" mb="8px">
                {t('settings.modules.setupIntro.title')}
              </Text>
              <Text fontSize="13px" color="solid.600" lineHeight="1.6">
                {t('settings.modules.setupIntro.description')}
              </Text>
            </Box>

            {inflowSetup && (
              <InflowSetupEditor
                columns={inflowSetup.columns}
                onToggleVisible={(key) =>
                  updateInflowColumn(key, (column) => ({
                    ...column,
                    visible: column.locked ? true : !column.visible,
                  }))
                }
                onLabelChange={(key, value) =>
                  updateInflowColumn(key, (column) => ({
                    ...column,
                    label: value,
                  }))
                }
                onMove={moveInflowColumn}
                isSaving={saveInflowSetupMutation.isPending}
                isDefault={!!inflowSetupResponse?.is_default}
                onSave={() => inflowSetup && saveInflowSetupMutation.mutate(inflowSetup)}
                t={t}
              />
            )}

            {addEmployeeSetup && (
              <AddEmployeeSetupEditor
                fields={addEmployeeSetup.fields}
                onToggleVisible={(key) =>
                  updateAddEmployeeField(key, (field) => ({
                    ...field,
                    visible: field.locked ? true : !field.visible,
                    required: field.locked ? true : field.visible ? false : field.required,
                  }))
                }
                onToggleRequired={(key) =>
                  updateAddEmployeeField(key, (field) => ({
                    ...field,
                    required: field.locked ? true : !field.required,
                  }))
                }
                isSaving={saveAddEmployeeSetupMutation.isPending}
                isDefault={!!addEmployeeSetupResponse?.is_default}
                onSave={() => addEmployeeSetup && saveAddEmployeeSetupMutation.mutate(addEmployeeSetup)}
                t={t}
              />
            )}

          </VStack>
        )}
      </Box>
    </Box>
  )
}

export default ModulesSettings
